"use client";

import { ref, onValue, off, set, update, remove } from "firebase/database";
import { collection, doc, setDoc } from "firebase/firestore";
import { rtdb, db } from "@/lib/firebase";
import type { MatchInvitation, MatchDetails, OpponentProfile } from "@/components/matchmaking/types";
import type { Match } from "@/types/match";
import { invitationToMatchData, generateMatchId } from "@/lib/utils/match-utils";

interface InvitationCallbacks {
  onInvitationReceived?: (invitation: MatchInvitation, opponent: OpponentProfile) => void;
  onInvitationAccepted?: (invitation: MatchInvitation) => void;
  onInvitationDeclined?: (invitation: MatchInvitation) => void;
  onMatchConfirmed?: (invitation: MatchInvitation) => void;
}

export class MatchInvitationService {
  private static instance: MatchInvitationService;
  private listeners: Map<string, () => void> = new Map();
  private callbacks: InvitationCallbacks = {};

  static getInstance(): MatchInvitationService {
    if (!MatchInvitationService.instance) {
      MatchInvitationService.instance = new MatchInvitationService();
    }
    return MatchInvitationService.instance;
  }

  /**
   * Send a match invitation with sequential acceptance flow
   * Step 1: Send to first user, wait for accept
   * Step 2: Only if accepted, notify second user
   */
  async sendMatchInvitation(
    challengerId: string,
    targetOpponentId: string,
    matchDetails: MatchDetails,
    challengerProfile: OpponentProfile
  ): Promise<string> {
    if (!rtdb) {
      throw new Error("Realtime database not initialized");
    }

    const invitationId = `invitation_${Date.now()}_${Math.random()
      .toString(36)
      .substr(2, 9)}`;

    const invitation: MatchInvitation = {
      id: invitationId,
      challengerId,
      challengedId: targetOpponentId,
      status: "pending_first_accept",
      matchDetails,
      createdAt: new Date().toISOString(),
      expiresAt: new Date(Date.now() + 10 * 60 * 1000).toISOString(), // 10 minutes
    };

    // Store invitation in RTDB
    const invitationRef = ref(rtdb, `matchInvitations/${invitationId}`);
    await set(invitationRef, invitation);

    // Add to target user's pending invitations
    const userInvitationRef = ref(rtdb, `userInvitations/${targetOpponentId}/${invitationId}`);
    await set(userInvitationRef, {
      invitationId,
      type: "match_invitation",
      from: challengerId,
      status: "pending_first_accept",
      createdAt: invitation.createdAt,
      expiresAt: invitation.expiresAt,
      challengerProfile, // Include challenger's profile for immediate display
    });

    // Set up expiration cleanup
    setTimeout(() => {
      this.expireInvitation(invitationId);
    }, 10 * 60 * 1000);

    return invitationId;
  }

  /**
   * Accept a match invitation (first acceptance)
   * This triggers the second user notification
   */
  async acceptMatchInvitation(
    invitationId: string,
    acceptingUserId: string
  ): Promise<void> {
    if (!rtdb) {
      throw new Error("Realtime database not initialized");
    }

    const invitationRef = ref(rtdb, `matchInvitations/${invitationId}`);
    
    // Get current invitation
    const invitation = await new Promise<MatchInvitation>((resolve, reject) => {
      onValue(invitationRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          resolve(data);
        } else {
          reject(new Error("Invitation not found"));
        }
      }, { onlyOnce: true });
    });

    if (invitation.status !== "pending_first_accept") {
      throw new Error("Invitation is no longer available");
    }

    // Update invitation status to show first acceptance
    await update(invitationRef, {
      status: "pending_second_accept",
      firstAcceptedAt: new Date().toISOString(),
    });

    // Remove from first user's pending invitations
    const firstUserInvitationRef = ref(rtdb, `userInvitations/${acceptingUserId}/${invitationId}`);
    await remove(firstUserInvitationRef);

    // Now notify the second user (challenger)
    const secondUserId = invitation.challengerId;
    const secondUserInvitationRef = ref(rtdb, `userInvitations/${secondUserId}/${invitationId}`);
    
    // Get accepting user's profile for display to second user
    const acceptingUserProfile = await this.getUserProfile(acceptingUserId);
    
    await set(secondUserInvitationRef, {
      invitationId,
      type: "match_acceptance",
      from: acceptingUserId,
      status: "pending_second_accept",
      createdAt: new Date().toISOString(),
      expiresAt: new Date(Date.now() + 5 * 60 * 1000).toISOString(), // 5 minutes for second accept
      acceptingUserProfile,
      message: `${acceptingUserProfile.displayName} accepted your match challenge!`,
    });

    // Send browser notification to second user
    this.sendBrowserNotification(
      secondUserId, 
      "Match Accepted! ✅",
      `${acceptingUserProfile.displayName} accepted your match challenge. Confirm to finalize the match!`
    );

    // Set shorter expiration for second acceptance
    setTimeout(() => {
      this.expireSecondAcceptance(invitationId);
    }, 5 * 60 * 1000);
  }

  /**
   * Accept the match invitation (second acceptance)
   * This finalizes and confirms the match
   */
  async confirmMatch(
    invitationId: string,
    confirmingUserId: string
  ): Promise<void> {
    if (!rtdb) {
      throw new Error("Realtime database not initialized");
    }

    const invitationRef = ref(rtdb, `matchInvitations/${invitationId}`);
    
    // Update invitation to confirmed status
    await update(invitationRef, {
      status: "confirmed",
      secondAcceptedAt: new Date().toISOString(),
    });

    // Remove from confirming user's pending invitations
    const userInvitationRef = ref(rtdb, `userInvitations/${confirmingUserId}/${invitationId}`);
    await remove(userInvitationRef);

    // Get the full invitation to notify both users
    const invitation = await new Promise<MatchInvitation>((resolve) => {
      onValue(invitationRef, (snapshot) => {
        resolve(snapshot.val());
      }, { onlyOnce: true });
    });

    // Create the actual match record
    const matchId = await this.createConfirmedMatch(invitation);

    // Notify both users that match is confirmed
    await Promise.all([
      this.notifyMatchConfirmed(invitation.challengerId, matchId, invitation),
      this.notifyMatchConfirmed(invitation.challengedId, matchId, invitation),
    ]);

    // Send browser notifications
    const [challenger, challenged] = await Promise.all([
      this.getUserProfile(invitation.challengerId),
      this.getUserProfile(invitation.challengedId),
    ]);

    this.sendBrowserNotification(
      invitation.challengerId,
      "Match Confirmed! 🎯",
      `Your match with ${challenged.displayName} is confirmed. Time to coordinate your tee time!`
    );

    this.sendBrowserNotification(
      invitation.challengedId,
      "Match Confirmed! 🎯", 
      `Your match with ${challenger.displayName} is confirmed. Time to coordinate your tee time!`
    );
  }

  /**
   * Decline a match invitation
   */
  async declineMatchInvitation(
    invitationId: string,
    decliningUserId: string,
    reason?: string
  ): Promise<void> {
    if (!rtdb) {
      throw new Error("Realtime database not initialized");
    }

    const invitationRef = ref(rtdb, `matchInvitations/${invitationId}`);
    
    // Update invitation status
    await update(invitationRef, {
      status: "declined",
      declinedAt: new Date().toISOString(),
      declinedBy: decliningUserId,
      declineReason: reason,
    });

    // Remove from declining user's invitations
    const userInvitationRef = ref(rtdb, `userInvitations/${decliningUserId}/${invitationId}`);
    await remove(userInvitationRef);

    // Get invitation to notify other user
    const invitation = await new Promise<MatchInvitation>((resolve) => {
      onValue(invitationRef, (snapshot) => {
        resolve(snapshot.val());
      }, { onlyOnce: true });
    });

    // Notify the other user
    const otherUserId = decliningUserId === invitation.challengerId 
      ? invitation.challengedId 
      : invitation.challengerId;

    const decliningUser = await this.getUserProfile(decliningUserId);

    // Don't spam notifications - just clean up
    console.log(`Match declined by ${decliningUser.displayName}`);
  }

  /**
   * Subscribe to match invitations for a user
   */
  subscribeToInvitations(
    userId: string,
    callbacks: InvitationCallbacks
  ): () => void {
    if (!rtdb) {
      return () => {}; // Return no-op if RTDB not available
    }

    this.callbacks = { ...this.callbacks, ...callbacks };
    
    const userInvitationsRef = ref(rtdb, `userInvitations/${userId}`);
    
    const unsubscribe = onValue(userInvitationsRef, (snapshot) => {
      const invitations = snapshot.val();
      
      if (invitations) {
        Object.values(invitations).forEach((invitation: any) => {
          this.handleInvitationUpdate(invitation);
        });
      }
    });

    this.listeners.set(`user_invitations_${userId}`, () => {
      off(userInvitationsRef);
      unsubscribe();
    });

    return () => {
      const listener = this.listeners.get(`user_invitations_${userId}`);
      if (listener) {
        listener();
        this.listeners.delete(`user_invitations_${userId}`);
      }
    };
  }

  private handleInvitationUpdate(invitationData: any) {
    switch (invitationData.type) {
      case "match_invitation":
        if (this.callbacks.onInvitationReceived && invitationData.challengerProfile) {
          // This is a new invitation - show the invitation dialog
          this.callbacks.onInvitationReceived(
            {
              id: invitationData.invitationId,
              challengerId: invitationData.from,
              challengedId: "", // Will be filled by the receiving user
              status: invitationData.status,
              matchDetails: invitationData.matchDetails || {},
              createdAt: invitationData.createdAt,
              expiresAt: invitationData.expiresAt,
            } as MatchInvitation,
            invitationData.challengerProfile
          );
        }
        break;

      case "match_acceptance":
        if (this.callbacks.onInvitationAccepted) {
          // This is notification that someone accepted our challenge
          // Show confirmation dialog to challenger
          this.callbacks.onInvitationAccepted({
            id: invitationData.invitationId,
            challengerId: "", // Current user
            challengedId: invitationData.from,
            status: invitationData.status,
            matchDetails: invitationData.matchDetails || {},
            createdAt: invitationData.createdAt,
            expiresAt: invitationData.expiresAt,
            firstAcceptedAt: invitationData.createdAt,
          } as MatchInvitation);
        }
        break;
    }
  }

  private async createConfirmedMatch(invitation: MatchInvitation): Promise<string> {
    if (!db) {
      throw new Error("Firestore not initialized");
    }

    // Generate unique match ID
    const matchId = generateMatchId();
    
    // Transform invitation to match data
    const baseMatchData = invitationToMatchData(invitation);
    
    const matchData: Partial<Match> = {
      ...baseMatchData,
      id: matchId,
      status: "upcoming", // Set as upcoming, not confirmed
      createdBy: invitation.challengerId,
    };

    // Create match document in Firestore
    const matchRef = doc(db, "matches", matchId);
    await setDoc(matchRef, matchData);

    return matchId;
  }

  private async notifyMatchConfirmed(
    userId: string,
    matchId: string,
    invitation: MatchInvitation
  ) {
    const notificationRef = ref(rtdb!, `userNotifications/${userId}/${matchId}`);
    await set(notificationRef, {
      type: "match_confirmed",
      matchId,
      invitationId: invitation.id,
      message: "Your golf match has been confirmed!",
      createdAt: new Date().toISOString(),
    });
  }

  private async expireInvitation(invitationId: string) {
    if (!rtdb) return;

    const invitationRef = ref(rtdb, `matchInvitations/${invitationId}`);
    const invitation = await new Promise<MatchInvitation>((resolve) => {
      onValue(invitationRef, (snapshot) => {
        resolve(snapshot.val());
      }, { onlyOnce: true });
    });

    if (invitation && invitation.status === "pending_first_accept") {
      await update(invitationRef, {
        status: "expired",
        expiredAt: new Date().toISOString(),
      });

      // Clean up user invitations
      const userInvitationRef = ref(rtdb, `userInvitations/${invitation.challengedId}/${invitationId}`);
      await remove(userInvitationRef);
    }
  }

  private async expireSecondAcceptance(invitationId: string) {
    if (!rtdb) return;

    const invitationRef = ref(rtdb, `matchInvitations/${invitationId}`);
    const invitation = await new Promise<MatchInvitation>((resolve) => {
      onValue(invitationRef, (snapshot) => {
        resolve(snapshot.val());
      }, { onlyOnce: true });
    });

    if (invitation && invitation.status === "pending_second_accept") {
      await update(invitationRef, {
        status: "expired",
        expiredAt: new Date().toISOString(),
      });

      // Clean up user invitations for challenger
      const userInvitationRef = ref(rtdb, `userInvitations/${invitation.challengerId}/${invitationId}`);
      await remove(userInvitationRef);
    }
  }

  private async getUserProfile(userId: string): Promise<OpponentProfile> {
    // This would typically fetch from your user profile API
    // For now, return mock data
    return {
      id: userId,
      name: "Golf Player",
      displayName: "Golf Player", 
      mmr: 1500,
      skillTier: "intermediate",
      matchesPlayed: 25,
      winRate: 60,
      lastActive: "2 minutes ago",
      distance: 3.2,
      compatibilityScore: 85,
    } as OpponentProfile;
  }

  private sendBrowserNotification(userId: string, title: string, body: string) {
    if (typeof window !== "undefined" && "Notification" in window) {
      if (Notification.permission === "granted") {
        new Notification(title, {
          body,
          icon: "/icon-192x192.png",
          tag: `match-invite-${userId}`,
          requireInteraction: true,
          data: { type: "match_invitation", userId },
        });
      }
    }
  }
}
